import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AmountPipe } from '../../../../../core/pipes/amount.pipe';
import { AuthService } from '../../../../auth/auth.service';
import { DashboardEngineService } from '../../../services/dashboard-engine.service';

@Component({
  selector: 'app-payment-methods-card',
  standalone: true,
  imports: [NgClass, AmountPipe, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './payment-methods-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentMethodsCardComponent implements OnChanges {
  private engine = inject(DashboardEngineService);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() dashboardType!: string;
  @Input() widgetId = 'paymentMethods';
  @Input() filters: any = {};
  @Input() requiredPermission?: string;

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  data: any = null;
  loading = false;
  error: string | null = null;

  // Colour palette for each method row — cycles if more methods exist
  readonly COLORS = [
    { bar: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700', icon: 'payments' },
    { bar: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700', icon: 'receipt_long' },
    { bar: 'bg-orange-400', badge: 'bg-orange-100 text-orange-700', icon: 'credit_card' },
    { bar: 'bg-violet-500', badge: 'bg-violet-100 text-violet-700', icon: 'account_balance' },
    { bar: 'bg-rose-500', badge: 'bg-rose-100 text-rose-700', icon: 'language' },
    { bar: 'bg-teal-500', badge: 'bg-teal-100 text-teal-700', icon: 'swap_horiz' },
  ];

  readonly METHOD_ICONS: Record<string, string> = {
    Cash: 'payments',
    Cheque: 'receipt_long',
    'Credit Card': 'credit_card',
    'Direct Deposit': 'account_balance',
    'Online Payment': 'language',
    'Bank Transfer': 'swap_horiz',
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasPermission) return;
    if (this.dashboardType) this.fetchData();
  }

  private fetchData(): void {
    this.loading = true;
    this.error = null;
    this.engine
      .getWidgetData(this.dashboardType, this.widgetId, this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.data = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.status === 403 ? 'Access restricted' : 'Unable to load data';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  getColor(index: number) {
    return this.COLORS[index % this.COLORS.length];
  }

  getMethodIcon(method: string): string {
    return this.METHOD_ICONS[method] ?? 'payments';
  }
}
